import { Directive, ElementRef, Input, NgZone, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';

/**
 * Co stoi na kafelku, dopóki wartość nie jest znana. Dywiz, bo tak oznacza brak danych
 * reszta widoków (np. `{{ total_users || '-' }}` w karcie opiekuna).
 */
const NO_VALUE = '-';

/**
 * Licznik, który dobija do wartości zamiast w nią wskakiwać.
 *
 * Dyrektywa, a nie komponent, bo kafelki statystyk mają własną typografię przypiętą do
 * klasy elementu (`stat-number-panel`, `count-value`). Komponent wniósłby swój markup i
 * zabrał tę klasę, więc wpięcie oznaczałoby przestylowanie każdego kafelka z osobna.
 * Tutaj element zostaje ten sam — dochodzi wyłącznie atrybut, a interpolacja z wnętrza
 * znika, bo tekst wypisuje już dyrektywa:
 *
 *     <p class="stat-number-panel" [appCountUp]="total_users"></p>
 *
 * Brak wartości (`null`/`undefined`) to NIE jest zero. Dopóki statystyki nie wrócą z API,
 * kafelek pokazuje kreskę — zero byłoby liczbą konkretną, wiarygodną i nieprawdziwą, a
 * przy wolnym endpoincie wisi na ekranie sekundami. Dopiero gdy wartość przyjdzie,
 * licznik przelicza od zera do niej.
 *
 * Animacja leci poza strefą Angulara: piszemy prosto do `textContent`, więc detekcja
 * zmian na każdej klatce byłaby czystym kosztem — a na dashboardzie takich liczników
 * jest kilka naraz.
 *
 * Kolejna animacja startuje z tego, co aktualnie widać. Bez tego odświeżenie dashboardu
 * zrzucałoby wszystkie liczniki do zera i odliczało je od nowa, co przy niewielkiej
 * zmianie wartości wygląda jak awaria.
 */
@Directive({
  selector: '[appCountUp]'
})
export class CountUpDirective implements OnChanges, OnDestroy {

  /** Wartość docelowa. `null`/`undefined` znaczy „jeszcze nie wiem”, nie „zero”. */
  @Input('appCountUp') value: number | null;

  /** Czas dobijania w milisekundach. */
  @Input() countUpDuration = 600;

  /** Ostatnia wypisana liczba, albo `null`, gdy na ekranie stoi kreska. */
  private shown: number | null = null;

  private frame: number | null = null;

  constructor(private host: ElementRef<HTMLElement>, private ngZone: NgZone) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.value) {
      return;
    }

    const target = this.parseTarget(this.value);

    if (target === null) {
      this.renderNoValue();
      return;
    }

    this.animateTo(target);
  }

  ngOnDestroy(): void {
    this.cancel();
  }

  private animateTo(target: number): void {
    this.cancel();

    // Kreska na ekranie znaczy, że nie pokazaliśmy jeszcze żadnej liczby — liczymy od zera.
    const from = this.shown === null ? 0 : this.shown;

    // Bez ruchu, gdy nie ma czego animować albo gdy użytkownik prosił o spokój (WCAG 2.3.3).
    // Wartość i tak musi się pojawić — znika tylko dochodzenie do niej.
    if (target === from || this.prefersReducedMotion() || typeof requestAnimationFrame === 'undefined') {
      this.render(target);
      return;
    }

    const distance = target - from;
    const duration = Math.max(this.countUpDuration, 1);
    let startedAt: number = null;

    this.render(from);

    this.ngZone.runOutsideAngular(() => {
      const step = (now: number) => {
        if (startedAt === null) {
          startedAt = now;
        }

        const progress = Math.min((now - startedAt) / duration, 1);
        // ease-out: szybki start, miękkie dojście do wartości.
        const eased = 1 - Math.pow(1 - progress, 3);

        this.render(Math.round(from + distance * eased));
        this.frame = progress < 1 ? requestAnimationFrame(step) : null;
      };

      this.frame = requestAnimationFrame(step);
    });
  }

  private render(value: number): void {
    this.shown = value;
    this.host.nativeElement.textContent = String(value);
  }

  private renderNoValue(): void {
    this.cancel();
    this.shown = null;
    this.host.nativeElement.textContent = NO_VALUE;
  }

  private cancel(): void {
    if (this.frame !== null) {
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }
  }

  /** Liczba do pokazania albo null, gdy wartość jest jeszcze nieznana. */
  private parseTarget(value: number | null): number | null {
    if (value === null || value === undefined) {
      return null;
    }

    const rounded = Math.round(Number(value));
    return isFinite(rounded) ? rounded : null;
  }

  private prefersReducedMotion(): boolean {
    return typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}
